import React, { useState, useMemo, useEffect } from "react";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useTheme, Box, Grid, Stack, Button, IconButton, Checkbox, Chip } from "@mui/material";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { tokens } from "../../assets/theme";
import messages from "./messages";
import { FormattedMessage } from "react-intl";
import { fetchOeeDashboardDataTimeSpan } from "../../fetchers/oee";
import { fetchCompany } from "../../fetchers/company";
import { useQuery } from "@tanstack/react-query";
import AxionControls from "../../components/controls/AxionControls";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { DndContext } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import OeeSummarize from "../home/components/oeeSummarize";
import { useOeeNotificationsForCompany} from "../../hooks/useOeeNotifications";
import { getTimespan } from "../../fetchers/timespan";
import { fetchUncodedStopsAndStatus } from "../../fetchers/machine";

export default function MachinesStatuses(props) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);
    const [timeSpan, setTimeSpan] = useState("currentshift");
    const [showMachineFilter, setShowMachineFilter] = useState(false);
    const [selectedMachines, setSelectedMachines] = useLocalStorage("machinesStatusSelectedMachines", []);
    const [showFields, setShowFields] = useState(false);
    const avalibleFields = useMemo(
         () => [
             "TAK",
             "T",
             "A",
             "K",
             "Last CT",
             "Last CT (color)",
             "Avg CT",
             "Avg CT (color)",
             "Ideal CT",
             "Takt"
         ],
         []
     );
    const [selectedFields, setSelectedFields] = useLocalStorage("machinesStatusSelectedFields", ["TAK"]);
    const [isFullscreen, setIsFullscreen] = useLocalStorage("machinesStatusIsFullscreen", false);
    const [machinesSortOrder, setMachinesSortOrder] = useLocalStorage("machinesStatusSortOrder", []);
    const [showStopTypes, setShowStopTypes] = useState(false);
    const [hideMachinesInNoOeeStop, setHideMachinesInNoOeeStop] = useLocalStorage("machinesStatusHideMachinesInNoOeeStop", false);
    const [showSummary, setShowSummary] = useState(false);
    const [oeeShowSummary, setOeeShowSummary] = useLocalStorage("machinesStatusOeeShowSummary", false);
    const [timeSpanStartDate, setTimeSpanStartDate] = React.useState(null);
    const [timeSpanEndDate, setTimeSpanEndDate] = React.useState(null);

    const currentValue = timeSpan;
    const onChange = (val) => setTimeSpan(val);

    useOeeNotificationsForCompany(companyKey);

    const toggleFullscreen = () => {
        setIsFullscreen((prev) => !prev);
    };

    const timespanQuery = useQuery({
        queryKey: ["oeedata", timeSpan, companyKey],
        queryFn: () => fetchOeeDashboardDataTimeSpan(timeSpan, companyKey),
        staleTime: 30000,
        refetchInterval: 30000,
        enabled: true,
        keepPreviousData: true
    });

    const companyQuery = useQuery({
        queryKey: ["company", companyKey],
        queryFn: () => fetchCompany(companyKey),
        staleTime: 60000,
        refetchInterval: 60000,
        enabled: true,
        keepPreviousData: true
    });

    const datesForTimespanQuery = useQuery(
        ["datesForTimespan", timeSpan, companyKey],
        () => getTimespan(timeSpan, companyKey),
        {
            staleTime: 60000,
            refetchInterval: 60000,
            enabled: true,
            keepPreviousData: true
        }
    );

    const uncodedStopsAndStatusQuery = useQuery(
        ["uncodedStopsAndStatus", companyKey, timeSpanStartDate, timeSpanEndDate], 
        () => fetchUncodedStopsAndStatus(timeSpanStartDate, timeSpanEndDate, companyKey),
        {
            staleTime: 60000,
            refetchInterval: 60000,
            enabled: timeSpanStartDate !== null && timeSpanEndDate !== null,
            keepPreviousData: true
        }
    );

    useEffect(() => {
        if (datesForTimespanQuery.isFetched && timeSpan !== 'manual' && !datesForTimespanQuery.isError && datesForTimespanQuery.data !== undefined) {
            setTimeSpanStartDate(datesForTimespanQuery.data[0].fromDate);
            setTimeSpanEndDate(datesForTimespanQuery.data[0].toDate);
        }
    }, [datesForTimespanQuery.isFetched, datesForTimespanQuery.isError, datesForTimespanQuery.data, timeSpan]);


    const filteredMachinesData = useMemo(() => {
        return timespanQuery.data?.map((item) => ({
            id: item.id,
            machineName: item.machineName,
        })) ?? [];
    }, [timespanQuery.data]);

    const items = useMemo(() => {
        if (!timespanQuery.data) {
            return [];
        }

        let result = timespanQuery.data.filter((item) =>
            selectedMachines.includes(item.machineName)
        );

        if (hideMachinesInNoOeeStop) {
            result = result.filter((item) => item.plannedStop !== "1");
        }

        if (machinesSortOrder.length > 0) {
            const orderMap = new Map(
                machinesSortOrder.map((id, index) => [id, index])
            );

            result = [...result].sort((a, b) => {
                const aIndex = orderMap.has(a.id) ? orderMap.get(a.id) : Number.MAX_SAFE_INTEGER;
                const bIndex = orderMap.has(b.id) ? orderMap.get(b.id) : Number.MAX_SAFE_INTEGER;
                return aIndex - bIndex;
            });
        }

        return result;
    }, [timespanQuery.data, selectedMachines, hideMachinesInNoOeeStop, machinesSortOrder]);

    const handleMachineChange = (machine) => {
        setSelectedMachines((prev) =>
            prev.includes(machine)
                ? prev.filter((item) => item !== machine)
                : [...prev, machine]
        );
    };

    const handleFieldsChange = (field) => {
        setSelectedFields((prev) =>
            prev.includes(field)
                ? prev.filter((item) => item !== field)
                : [...prev, field]
        );
    };

    const timeOptions = [
        { value: "currentshift", label: <FormattedMessage {...messages.currentShiftFilter} /> },
        { value: "prevShift", label: <FormattedMessage {...messages.prevShiftFilter} /> },
        { value: "24h", label: <FormattedMessage {...messages.dayFilter} /> },
        { value: "72h", label: <FormattedMessage {...messages.threeDayFilter} /> },
    ];

    const timespanPicker = (
        <Stack direction="row" spacing={1}>
            {timeOptions.map((opt) => (
                <Chip
                    key={opt.value}
                    label={opt.label}
                    variant={currentValue === opt.value ? "filled" : "outlined"}
                    color={currentValue === opt.value ? "success" : "default"}
                    onClick={() => onChange(opt.value)}
                    sx={{
                        cursor: "pointer",
                        fontWeight: currentValue === opt.value ? 600 : 400,
                        "&.MuiChip-filled": {
                            backgroundColor: "#799c4a",
                            color: "#fff",
                        },
                    }}
                />
            ))}
        </Stack>
    );

    function calculateMetrics(items) {
        if (!items || items.length === 0) {
            return { avgT: 0, avgA: 0, avgK: 0, total: 0 };
        }

        const { tSum, aSum, kSum } = items.reduce(
            (acc, item) => {
                acc.tSum += item.t ?? 0;
                acc.aSum += item.a ?? 0;
                acc.kSum += item.k ?? 0;
                return acc;
            },
            { tSum: 0, aSum: 0, kSum: 0 }
        );

        const avgT = tSum / items.length;
        const avgA =  Math.min(aSum / items.length, 100);
        const avgK = kSum / items.length;

        const total = avgT * avgA * avgK / 10000;

        return { avgT, avgA, avgK, total };
    }

    const { avgT, avgA, avgK, total } = useMemo(
        () => calculateMetrics(items),
        [items]
    );

    const getMetricColor = (value, badThreshold, warningThreshold, colors) => {
        if (value < badThreshold) return colors.redAccent[600];
        if (value < warningThreshold) return colors.yellow[600];
        return colors.dashbordGreen[600];
    };

    const metricColors = useMemo(() => {
        const oeeBad = companyQuery.data?.oeePercentBad ?? 0;
        const oeeWarning = companyQuery.data?.oeePercentWarning ?? 0;

        const availabilityPercentBad =
            companyQuery.data?.availabilityPercentBad ?? oeeBad;
        const availabilityPercentWarning =
            companyQuery.data?.availabilityPercentWarning ?? oeeWarning;

        const performancePercentBad =
            companyQuery.data?.performancePercentBad ?? oeeBad;
        const performancePercentWarning =
            companyQuery.data?.performancePercentWarning ?? oeeWarning;

        const qualityPercentBad =
            companyQuery.data?.qualityPercentBad ?? oeeBad;
        const qualityPercentWarning =
            companyQuery.data?.qualityPercentWarning ?? oeeWarning;

        return {
            totalColor: getMetricColor(total, oeeBad, oeeWarning, colors),
            totalTColor: getMetricColor(
                avgT,
                availabilityPercentBad,
                availabilityPercentWarning,
                colors
            ),
            totalAColor: getMetricColor(
                avgA,
                performancePercentBad,
                performancePercentWarning,
                colors
            ),
            totalKColor: getMetricColor(
                avgK,
                qualityPercentBad,
                qualityPercentWarning,
                colors
            ),
        };
    }, [total, avgT, avgA, avgK, companyQuery.data, colors]);

    if (timespanQuery.isLoading || companyQuery.isLoading || datesForTimespanQuery.isLoading || uncodedStopsAndStatusQuery.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />;
    }

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!active || !over || active.id === over.id) {
            return;
        }

        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        const reorderedItems = arrayMove(items, oldIndex, newIndex);
        setMachinesSortOrder(reorderedItems.map((item) => item.id));
    };

    return (
        <Box
            m="20px"
            sx={{
                minHeight: "90vh",
                backgroundColor: colors.primary[700],
            }}
            className={`component-container ${isFullscreen ? "fullscreen" : ""}`}
        >
            <Box>
                <Grid container spacing={0} sx={{ paddingBottom: "40px" }}>
                    <Grid item xs={12} mt={3} ml={2}>
                        <Stack direction="row" alignItems="center" sx={{ width: "100%" }} justifyContent="space-between">
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ flexGrow: 1 }}>
                                <IconButton onClick={toggleFullscreen}>
                                    {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                </IconButton>

                                <FilterAltIcon />

                                <Button
                                    sx={{ color: colors.primary[200] }}
                                    onClick={() => setShowMachineFilter((prev) => !prev)}
                                >
                                    <FormattedMessage {...messages.machinesFilter} />{" "}
                                    {showMachineFilter ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </Button>

                                <Button
                                    sx={{ color: colors.primary[200] }}
                                    onClick={() => setShowStopTypes((prev) => !prev)}
                                >
                                    <FormattedMessage {...messages.filters} />{" "}
                                    {showStopTypes ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </Button>

                                <Button
                                    sx={{ color: colors.primary[200] }}
                                    onClick={() => setShowFields((prev) => !prev)}
                                >
                                    <FormattedMessage {...messages.fieldsToShow} />{" "}
                                    {showFields ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </Button>

                                <Button
                                    sx={{ color: colors.primary[200] }}
                                    onClick={() => setShowSummary((prev) => !prev)}
                                >
                                    <FormattedMessage {...messages.summary} />{" "}
                                    {showSummary ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </Button>

                            </Stack>

                            <Box sx={{ display: "flex" }} mr={4}>
                                {timespanPicker}
                            </Box>
                        </Stack>

                        {showMachineFilter && (
                            <Box mb={2} pl={2}>
                                {[...filteredMachinesData]
                                    .sort((a, b) => a.machineName.localeCompare(b.machineName))
                                    .map((machine) => (
                                        <FormControlLabel
                                            key={machine.machineName}
                                            control={
                                                <Checkbox
                                                    sx={{
                                                        color: "secondary",
                                                        "&.Mui-checked": {
                                                            color: colors.dashbordGreen[500],
                                                        },
                                                    }}
                                                    checked={selectedMachines.includes(machine.machineName)}
                                                    onChange={() => handleMachineChange(machine.machineName)}
                                                />
                                            }
                                            label={machine.machineName}
                                        />
                                    ))}
                            </Box>
                        )}

                        {showStopTypes && (
                            <Box mb={2} pl={2}>
                                <FormControlLabel
                                    key="NoOeeStop"
                                    control={
                                        <Checkbox
                                            sx={{
                                                color: "secondary",
                                                "&.Mui-checked": {
                                                    color: colors.dashbordGreen[500],
                                                },
                                            }}
                                            checked={hideMachinesInNoOeeStop}
                                            onChange={() => setHideMachinesInNoOeeStop((prev) => !prev)}
                                        />
                                    }
                                    label={<FormattedMessage {...messages.dontShowMachinesInNoOeeStop} />}
                                />
                            </Box>
                        )}

                        {showFields && (
                            <Box mb={2} pl={2}>
                                {avalibleFields.map((field) => (
                                    <FormControlLabel
                                        key={field}
                                        control={
                                            <Checkbox
                                                sx={{
                                                    color: "secondary",
                                                    "&.Mui-checked": {
                                                        color: colors.dashbordGreen[500],
                                                    },
                                                }}
                                                checked={selectedFields.includes(field)}
                                                onChange={() => handleFieldsChange(field)}
                                            />
                                        }
                                        label={
                                            field === "TAK" ? (
                                                <FormattedMessage {...messages.oee} />
                                            ) : field === "T" ? (
                                                <FormattedMessage {...messages.a} />
                                            ) : field === "A" ? (
                                                <FormattedMessage {...messages.p} />
                                            ) : field === "K" ? (
                                                <FormattedMessage {...messages.q} />
                                            ) : (
                                                field
                                            )
                                        }
                                    />
                                ))}
                            </Box>
                        )}
                        {showSummary && (
                            <Box mb={2} pl={2}>
                                <FormControlLabel
                                    key="OeeSummary"
                                    control={
                                        <Checkbox
                                            sx={{
                                                color: "secondary",
                                                "&.Mui-checked": {
                                                    color: colors.dashbordGreen[500],
                                                },
                                            }}
                                            checked={oeeShowSummary}
                                            onChange={() => setOeeShowSummary((prev) => !prev)}
                                        />
                                    }
                                    label={<FormattedMessage {...messages.oeeSummary} />}
                                />
                            </Box>
                        )}
                    </Grid>

                    {oeeShowSummary && (
                        <Grid item xs={12} mt={3} ml={2}>
                            <OeeSummarize
                                totalT={avgT}
                                totalA={avgA}
                                totalK={avgK}
                                total={total}
                                totalTColor={metricColors.totalTColor}
                                totalAColor={metricColors.totalAColor}
                                totalKColor={metricColors.totalKColor}
                                totalColor={metricColors.totalColor}

                            />
                        </Grid>
                    )}

                    <DndContext onDragEnd={handleDragEnd}>
                        <SortableContext items={items.map((item) => item.id)} strategy={rectSortingStrategy}>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                    gap: 2,
                                    p: 1,
                                    m: 1,
                                    width: "100%",
                                    flexWrap: "wrap",
                                }}
                            >
                                {items.length > 0 &&
                                    items.map((item) => (
                                        <AxionControls.AxionMachineCard
                                            key={item.id}
                                            id={item.id}
                                            item={item}
                                            fields={selectedFields}
                                            uncodedStopsAndStatus={uncodedStopsAndStatusQuery?.data?.find(stop => stop.machineId === item.id)}
                                        />
                                    ))}
                            </Box>
                        </SortableContext>
                    </DndContext>
                </Grid>
            </Box>
        </Box>
    );
}